import {
  Button,
  SvgIcon,
  SvgNames,
  tokens,
  Tooltip,
  Typography,
} from "@sunrun/experience-ui-components";
import { useState } from "react";
import styled from "@emotion/styled/macro";

type FryProps = {
  label: string;
  action: () => void;
  disabled?: boolean;
  tooltip?: string;
  /** Show the tooltip for the whole time the menu is open. */
  showTooltipWhileOpen?: boolean;
  hideIcon?: boolean;
};

type HamburgerMenuProps = {
  fries: FryProps[];
  isMobile?: boolean;
  buttonLabel?: string;
};

const HamburgerMenu = ({
  fries,
  isMobile,
  buttonLabel,
}: HamburgerMenuProps) => {
  const [showFries, setShowFries] = useState(false);
  const isLabeled = Boolean(buttonLabel);

  const menuItems = showFries && (
    <Fries isLabeled={isLabeled}>
      {fries.map((fry: FryProps, index) => {
        const fryContent = (
          <FryContent disabled={fry.disabled}>
            <Typography
              fontWeight={"medium"}
              size={16}
              lineHeight={24}
              color={tokens.TINTS_HERO_BLUE_50}
            >
              {fry.label}
            </Typography>
            {!fry.hideIcon && (
              <span data-testid="fry-external-link-icon">
                <SvgIcon
                  width={16}
                  height={16}
                  color={tokens.TINTS_HERO_BLUE_50}
                  name={SvgNames.ExternalLink}
                />
              </span>
            )}
          </FryContent>
        );

        return (
          <Fry
            index={index}
            key={fry.label}
            disabled={fry.disabled}
            role={fry.disabled ? undefined : "button"}
            aria-disabled={fry.disabled || undefined}
            onClick={() => {
              if (fry.disabled) return;
              fry.action();
              setShowFries(false);
            }}
          >
            {fry.disabled && fry.tooltip ? (
              <FryTooltip
                className={
                  fry.showTooltipWhileOpen ? "fry-tooltip-open" : undefined
                }
                content={fry.tooltip}
              >
                {fryContent}
              </FryTooltip>
            ) : (
              fryContent
            )}
          </Fry>
        );
      })}
    </Fries>
  );

  if (isLabeled) {
    return (
      <MenuContainer>
        <LabeledTrigger
          onClick={() => setShowFries(!showFries)}
          aria-expanded={showFries}
          aria-haspopup="menu"
        >
          <Typography
            fontWeight="medium"
            size={16}
            lineHeight={24}
            color={tokens.BRAND_HERO_BLUE}
          >
            {buttonLabel}
          </Typography>
          <SvgIcon
            width={24}
            height={24}
            color={tokens.BRAND_HERO_BLUE}
            name={showFries ? SvgNames.ChevronUp : SvgNames.ChevronDown}
          />
        </LabeledTrigger>
        {menuItems}
      </MenuContainer>
    );
  }

  return (
    <>
      <Hamburger
        onClick={() => setShowFries(!showFries)}
        isMobile={isMobile || false}
      >
        <SvgIcon
          width={24}
          height={24}
          color={isMobile ? tokens.WHITE : tokens.BRAND_MEDIUM_BLUE}
          name={SvgNames.VerticalDotsSvg}
        />
      </Hamburger>
      {menuItems}
    </>
  );
};

const Hamburger = styled(Button)<{ isMobile: boolean }>`
  background: unset;
  display: flex;
  border: 1px solid
    ${({ isMobile }) => (isMobile ? tokens.WHITE : tokens.BRAND_MEDIUM_BLUE)};
  width: 32px;
  height: 32px;
  border-radius: 50%;
  padding: 0;

  & > span {
    display: flex;
  }
`;

const MenuContainer = styled.div`
  position: relative;
  display: inline-flex;
`;

const LabeledTrigger = styled(Button)`
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1.5px solid ${tokens.BRAND_HERO_BLUE};
  border-radius: 800px;
  padding: 4px 12px;
  height: auto;
  width: auto;
  min-width: unset;

  & > span {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const fryWidth = "208px";

const Fries = styled.ul<{ isLabeled: boolean }>`
  display: inline-block;
  list-style: none;
  background-color: ${tokens.TINTS_OFF_WHITE_90};
  padding-inline-start: 0;
  z-index: 1005;
  border-radius: 10px;
  background: var(--white, #fff);
  box-shadow: 0px 6px 16px 0px rgba(0, 0, 0, 0.1);

  ${({ isLabeled }) =>
    isLabeled
      ? `
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    margin: 0;
  `
      : `
    /* negative left margin of the fry width to offset ul */
    margin-left: -${fryWidth};
    position: sticky;
    right: 64px;
    margin-top: 16px;
  `}

  li:last-child {
    border-bottom: none;
  }
`;

const Fry = styled.li<{ index: number; disabled?: boolean }>`
  position: relative;
  display: block;
  min-width: ${fryWidth};
  padding: 12px 16px;
  align-items: center;
  gap: 8px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};

  height: 22px;
  ${({ index }) =>
    index !== 0 ? `border-top: 1px solid ${tokens.OFFWHITE_30};` : ""}
  & > svg {
    margin-left: 10px;
  }

  ${({ disabled }) =>
    !disabled
      ? `
    &:hover {
      p,
      svg {
        opacity: 70%;
      }
    }
  `
      : ""}
`;

const FryContent = styled.div<{ disabled?: boolean }>`
  display: flex;
  gap: 8px;
  align-items: center;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

const FryTooltip = styled(Tooltip)`
  && .sr-tooltip-content,
  && .sr-tooltip-content-mobile {
    padding: 16px;
    z-index: 1006;
  }

  &.fry-tooltip-open > [role="tooltip"] {
    visibility: visible;
    opacity: 1;
    transform: translateX(-30px);
  }
`;

export { HamburgerMenu };
export type { FryProps };
